// `token` is the emailed preferences link's single-use session token (ENG-3878),
// still unspent while the page loads — a copy in Datadog would be replayable.
const CREDENTIAL_QUERY_PARAMS = ['token', 'jwt', 'code', 'access_token', 'id_token', 'refresh_token'];

export const REDACTED = 'redacted';

const splitOn = (value: string, separator: string): [string, string | undefined] => {
  const index = value.indexOf(separator);
  return index === -1 ? [value, undefined] : [value.slice(0, index), value.slice(index + 1)];
};

/** Redacted query string, or undefined when it carried no credential. */
const redactQueryString = (query: string): string | undefined => {
  const params = new URLSearchParams(query);

  let didRedact = false;
  Array.from(params.keys()).forEach((key) => {
    if (CREDENTIAL_QUERY_PARAMS.includes(key.toLowerCase())) {
      // `set` also collapses repeats, dropping any further copies.
      params.set(key, REDACTED);
      didRedact = true;
    }
  });

  return didRedact ? params.toString() : undefined;
};

/**
 * Splits a fragment into the part to keep verbatim and the query-shaped part to
 * scan. `#access_token=...` is itself a query; `#/route?token=...` puts one after
 * a `?`; a plain `#section` has none — reserialising that would yield `#section=`.
 */
const splitHashQuery = (hash: string): [string, string | undefined] => {
  const [beforeQuestion, afterQuestion] = splitOn(hash, '?');
  if (afterQuestion !== undefined) return [`${beforeQuestion}?`, afterQuestion];
  return hash.includes('=') ? ['', hash] : [hash, undefined];
};

/**
 * Redacts credential-bearing params from a URL's query and fragment, keeping the
 * rest (UTM params are much of why these get logged). The fragment matters as much
 * as the query: implicit-flow credentials arrive as `#access_token=...`, and it is
 * the half a server never sees but RUM records in full.
 *
 * String-based rather than `new URL()` because callers pass origin-less request
 * paths as well as absolute URLs.
 */
export const redactSensitiveParams = (url: string): string => {
  if (!url) return url;

  const [beforeHash, hash] = splitOn(url, '#');
  const [path, query] = splitOn(beforeHash, '?');
  const redactedQuery = query === undefined ? undefined : redactQueryString(query);

  const [hashPrefix, hashQuery] = hash === undefined ? ['', undefined] : splitHashQuery(hash);
  const redactedHashQuery = hashQuery === undefined ? undefined : redactQueryString(hashQuery);

  // Returned as-is rather than reserialised, so ordinary log lines are unchanged.
  if (redactedQuery === undefined && redactedHashQuery === undefined) return url;

  const nextQuery = redactedQuery ?? query;
  const nextHashQuery = redactedHashQuery ?? hashQuery;
  const nextHash = hash === undefined ? '' : `#${hashPrefix}${nextHashQuery ?? ''}`;
  return `${path}${nextQuery ? `?${nextQuery}` : ''}${nextHash}`;
};
